import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { match } from 'ts-pattern';

import DownArrow from '@/assets/icons/dropdown-arrow.svg?react';
import CheckIcon from '@/assets/icons/steps-completed.svg?react';
import InProgressIcon from '@/assets/icons/steps-in-progress.svg?react';
import PendingIcon from '@/assets/icons/steps-pending.svg?react';
import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly label: string;
  readonly status: 'completed' | 'in-progress' | 'pending';
  readonly date: string | null;
  readonly description: string;
}

export const TransactionDetailsDrawerStatusItem: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { label, status, date, description } = props;
  const [expanded, setExpanded] = React.useState(false);

  const statusIcon = React.useMemo(
    (): React.ReactElement =>
      match(status)
        .with('completed', (): React.ReactElement => <CheckIcon fill="currentColor" />)
        .with('in-progress', (): React.ReactElement => <InProgressIcon stroke="currentColor" />)
        .with('pending', (): React.ReactElement => <PendingIcon stroke="currentColor" />)
        .exhaustive(),
    [status],
  );

  const titleColor = React.useMemo(
    (): string =>
      match(status)
        .with('completed', (): string => 'custom.BlackText')
        .with('in-progress', (): string => 'primary.main')
        .with('pending', (): string => 'custom.Inactive')
        .exhaustive(),
    [status],
  );

  const handleToggle = React.useCallback((): void => {
    setExpanded((expanded: boolean): boolean => !expanded);
  }, []);

  const expanderStyle = React.useMemo((): readonly SystemStyleObject[] | SystemStyleObject => {
    if (expanded) {
      return [styles.iconButton, styles.rotatedButton];
    } else {
      return styles.iconButton;
    }
  }, [expanded]);

  const tr = useTranslator();

  return (
    <Box display="flex" flexDirection="column" alignItems="top" position="relative" zIndex={1}>
      <Box
        display="flex"
        alignItems="flex-start"
        gap={2}
        justifyContent="space-between"
        width="100%">
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Box height={16} zIndex={2} mt={0.5} color={titleColor}>
            {statusIcon}
          </Box>
          <Box display="flex" flexDirection="column">
            <Typography variant="body2" color={titleColor}>
              {tr(label)}
            </Typography>
            <Typography fontSize={14} variant="body2" color="textSecondary">
              {date}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" sx={expanderStyle} onClick={handleToggle}>
          <DownArrow width={18} height={18} fill="currentColor" />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box pl={4} pt={1}>
          <Typography variant="body2" color="textSecondary">
            {tr(description)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  rotatedButton: { transform: 'rotate(180deg)' },
  iconButton: {
    color: 'custom.LightGray',
    transition: 'transform 0.2s ease-in-out',
  },
};
